import { LocationStrategy } from "@angular/common";
import { inject, Injectable } from "@angular/core";
import { Router, RouterStateSnapshot, UrlTree } from "@angular/router";

import { catchError, map, Observable, of } from "rxjs";

import { LektoAuthService } from "../services/essential/lekto-auth.service";
import { EnrichmentConfigService } from "../services/enrichment/enrichment-config.service";
import { ImpersonationService } from "../services/identity/impersonation.service";
import { ProfileConstants } from "../constants/profile.constants";

@Injectable({ providedIn: "root" })
export class EnrichmentGuard {
  private readonly router = inject(Router);
  private readonly authService = inject(LektoAuthService);
  private readonly enrichmentConfigService = inject(EnrichmentConfigService);
  private readonly impersonationService = inject(ImpersonationService);
  private readonly locationStrategy = inject(LocationStrategy);

  public canActivate(_route: unknown, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    if (!this.authService.isLoggedIn()) {
      return of(true);
    }

    // CA02 (DEV-871): em sessão personificada o enriquecimento não se aplica — o gestor não vai
    // preencher dados pessoais do alvo, e o fluxo bloquearia o suporte que motivou a personificação.
    // Vem antes da consulta de propósito: liberar depois faria o app buscar as etapas do alvo só para
    // descartá-las.
    if (this.impersonationService.isImpersonating()) {
      return of(true);
    }

    // Recorte de rota (os perfis que têm a rota /enrichment), não a matriz de etapas: quais etapas se
    // aplicam ao perfil ativo é decisão do servidor (DEV-1175, `GET /api/user/config/steps`).
    const isEligibleProfile = this.authService.hasProfile(
      ProfileConstants.ENRICHMENT_ELIGIBLE_PROFILES
    );

    if (!isEligibleProfile) {
      return of(true);
    }

    if (this.enrichmentConfigService.isChecked()) {
      return of(true);
    }

    // "Fazer isso depois": o fluxo só volta no próximo login.
    if (this.enrichmentConfigService.isDeferredInSession()) {
      return of(true);
    }

    // DEV-1020: o app pode abrir sem rede (service worker) para retomar uma atribuição salva no
    // dispositivo. Sem como consultar as etapas, a checagem fica para a próxima navegação em rede —
    // barrar aqui deixaria a pessoa sem tela nenhuma.
    if (globalThis.navigator?.onLine === false) {
      return of(true);
    }

    // O `?.` é de verdade: sem o cookie de papel, `getProfileData()` devolve `null` (DEV-991).
    const idSchool = this.authService.getProfileData()?.idSchool;

    return this.enrichmentConfigService.fetchSteps(idSchool).pipe(
      map((response) => {
        if (!response.hasPendingSteps) {
          return true;
        }
        return this.router.createUrlTree(["/enrichment"], {
          queryParams: {
            mandatory: "true",
            returnUrl:
              globalThis.location.origin + this.locationStrategy.prepareExternalUrl(state.url)
          }
        });
      }),
      // Falha ao verificar as etapas não bloqueia: a navegação segue e a checagem fica para a
      // próxima navegação (o erro não entra no cache).
      catchError(() => of(true))
    );
  }
}
